import React, { useEffect, useState } from 'react'

import styled from "@emotion/styled"
import { css } from "@emotion/react"
import { useLocation, useNavigate } from '@reach/router'

import KYC from "../../services/kyc"
import { useAuth } from '../../hooks/auth-context'
import MP from "../../services/mercadopago"

import CustomButtonV2 from "../ui/custom-button-v2"
import LoadingScreenIcon from "../ui/images/loading-screen"

import successGif from "../../images/success.gif"


const ContainerStatus = styled.div`
  display: flex;
  justify-content: space-around;
  min-width: 600px;
`

const ContainerNuvei = styled.div`
  max-width: 600px;
  background-color: white;
  border: 1px solid #c4cdd5;
  border-radius: 5px;
  padding: 25px;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 auto;
`

const RowDetail = styled.div`
  display: flex;
  align-self: center;
  &>div:first-of-type {
    text-align: right;
    padding-right: 5px;
  }
  &>div:last-of-type {
    text-align: left;
    padding-left: 5px;
  }
  width: 100%;
`

const CellDetail = styled.div`
  width: 100%;
`

const DivWrapper = styled.div`
  text-align: center;
  margin: 0 auto;
  max-width: 556px;
  width: 100%;
  &.loading {
    display: flex;
    justify-content: center;
  }
  & * {
    font-family: "Open Sans";
  }
`

const Title = styled.h1`
  font-family: "Roboto";
  color: black;
  font-size: 24px;
  font-weight: 400;
  line-height: 30px;
  margin-bottom: 30px;
  margin-top: 0px;
  text-align: center;
`

const Info = styled.div`
  font-family: "Roboto";
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #080F12;
  text-align: center;
`

const buttonStyle = css`
  margin-top: 1rem;
  width: 298px;
  height: 50px;
  margin: 0 auto;
  @media (max-width: 425px) {
    width: 213px;
    font-size: 18px;
    line-height: 138.889%;
    text-align: center;
  }
`

const loadingButtonStyle = buttonStyle

type ApprovedComponentProps = {
  date: string
  amount: string
  currency: string
  paymentId: string
  email: string
}

const ApprovedComponent = ({
  date,
  amount,
  currency,
  paymentId,
  email,
}: ApprovedComponentProps) => {
  return (
    <>
      <img src={successGif} alt="Depósito Exitoso" width={150} />
      <Title>Depósito Exitoso</Title>
      <ContainerNuvei>
        <RowDetail>
          <CellDetail>N° Transacción: </CellDetail>
          <CellDetail>{paymentId}</CellDetail>
        </RowDetail>
        <RowDetail>
          <CellDetail>Fecha: </CellDetail>
          <CellDetail>{date}</CellDetail>
        </RowDetail>
        <RowDetail>
          <CellDetail>{currency} $: </CellDetail>
          <CellDetail>{amount}</CellDetail>
        </RowDetail>
      </ContainerNuvei>
      <Info>
        <p>
          Recuerde que se ha enviado automáticamente un comprobante de pago
          a su email <strong>{email}</strong>
        </p>
        <p>
          <strong>Importante:</strong> El monto depositado puede que demore
          un tiempo en verse reflejado en la plataforma. Ante cualquier duda
          puedes comunicarte con tu ejecutivo
        </p>
      </Info>
    </>
  )
}

type ErrorWithCreditCardProps = {
  paymentId: string
}

const ErrorWithCreditCard = ({
  paymentId,
}: ErrorWithCreditCardProps) => {
  return (
    <>
      <Title>Ocurrio un error al procesar el deposito</Title>
      <ContainerNuvei>
        <RowDetail>
          <CellDetail>ID de la Transacción: </CellDetail>
          <CellDetail>{paymentId}</CellDetail>
        </RowDetail>
        <RowDetail>
          Hubo un error al procesar el pago.
        </RowDetail>
        <RowDetail>
          Consulta con tu ejecutivo para obtener mayor información
        </RowDetail>
      </ContainerNuvei>
    </>
  )
}

const PendingView = () => (
  <>
    <Title>Pago en progreso</Title>
    <ContainerNuvei>
      <RowDetail>
        Si ya realizaste tu Pago, debes esperar unos minutos para que nos informe Mercado
        Pago.
      </RowDetail>
      <RowDetail>
        Te llegara un correo con la información correspondiente.
      </RowDetail>
    </ContainerNuvei>
  </>
)

type Props = {
  status?: string | null
  children?: React.ReactNode
}

type CheckPendingProps = {
  status?: string | null
  date: string
  amount: number
  currency: string
  paymentId: string | null
  email: string
  pending: boolean
  isLoading: boolean
  onSubmit: () => void
}

const CheckPending = ({
  status,
  date,
  amount,
  currency,
  paymentId,
  email,
  pending,
  isLoading,
  onSubmit,
}: CheckPendingProps) => {

  let componentView = null

  if (status === 'success') {
    if (pending) {
      componentView = <PendingView />
    } else {
      componentView = (
        <ApprovedComponent
          date={date}
          amount={amount.toString()}
          currency={currency}
          paymentId={paymentId || ''}
          email={email}
        />
      )
    }
  }

  if (status === 'pending') {
    componentView = <PendingView />
  }

  if (status === 'failure') {
    componentView = <ErrorWithCreditCard paymentId={paymentId || ''} />
  }

  return (
    <>
      {componentView}
      <CustomButtonV2
        style={buttonStyle}
        onClick={onSubmit}
        loading={isLoading}
        loadingStyle={loadingButtonStyle}
      >
        Continuar
      </CustomButtonV2>
    </>
  )
}

const MercadoPagoBack = ({ status = null }: Props) => {
  const { state: { user, setUser } } = useAuth()
  const location = useLocation() // Obtiene la ubicación actual
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search) // Extrae los parámetros de consulta
  const paymentId = queryParams.get('payment_id') // Obtiene el valor de payment_id

  const [isLoading, setIsLoading] = useState(false)
  const [loadingView, setLoadingView] = useState(false)
  const [amount, setAmount] = useState(0)
  const [date, setDate] = useState('')
  const [pending, setPending] = useState(false)

  // Llamada directa a getStatus si paymentId está disponible
  useEffect(() => {
    const fetchStatus = async () => {
      setLoadingView(true)
      if (paymentId && user) {
        try {
          console.log("paymentId: ", paymentId)
          const payment = await MP.getPayment(paymentId, user)
          console.log("payment", payment)
          setAmount(payment?.amount || 0)
          setDate(payment?.payment_date || '')
          if (payment == null) {
            setPending(true)
          }
          // console.log('Estado obtenido:', response)
          setLoadingView(false)
        } catch (error) {
          console.error('Error obteniendo el estado:', error)
        }
      }
    }

    fetchStatus()
  }, [paymentId, user])

  const onSubmit = async () => {
    if (!user) return

    setIsLoading(true)
    const result = await KYC.updateDeposit(user)
    setUser(result.user)
    setIsLoading(false)
    navigate(`/${result.user.kycNextStep}`)
  }

  const currency = "CLP"
  const email = user?.email || ""

  return (
    <ContainerStatus>
      <DivWrapper className={loadingView ? "loading" : ""}>
        {loadingView && <LoadingScreenIcon />}
        {!loadingView && <CheckPending status={status} date={date} amount={amount} currency={currency} paymentId={paymentId} email={email} pending={pending} isLoading={isLoading} onSubmit={onSubmit} />}

      </DivWrapper>
    </ContainerStatus>
  )
}

export default MercadoPagoBack
